/**
 * Hook para ejecutar tareas cron manualmente
 * Maneja estados de loading, error y éxito
 */
import { useState, useCallback } from 'react'
import toast from 'react-hot-toast'
import { CronTasksService } from '../../services/cron/cronTasksService'
import type { CronTaskExecuteParams, CronTaskExecuteResponse } from '../../types/cron/cronTasks'

interface UseCronTaskExecutionOptions {
  taskId: string
  endpoint: string
  /** GET para tareas Bold (verify links, voucher verify), por defecto POST */
  executeMethod?: 'GET' | 'POST'
  onSuccess?: (response: CronTaskExecuteResponse) => void
  onError?: (error: Error) => void
}

interface UseCronTaskExecutionReturn {
  execute: (params?: CronTaskExecuteParams) => Promise<void>
  isLoading: boolean
  error: string | null
}

/**
 * Hook que maneja la ejecución manual de tareas cron
 * Incluye manejo de errores y notificaciones
 */
export function useCronTaskExecution({
  taskId,
  endpoint,
  executeMethod = 'POST',
  onSuccess,
  onError,
}: UseCronTaskExecutionOptions): UseCronTaskExecutionReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const execute = useCallback(
    async (params?: CronTaskExecuteParams) => {
      setIsLoading(true)
      setError(null)

      try {
        const response = await CronTasksService.executeTask(
          taskId,
          endpoint,
          params,
          executeMethod
        )

        // Mostrar notificación de éxito
        toast.success(response.message || 'Tarea ejecutada correctamente', {
          duration: 4000,
        })

        // Llamar callback de éxito si existe
        if (onSuccess) {
          onSuccess(response)
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Error desconocido'
        setError(errorMessage)

        // Mostrar notificación de error
        toast.error(
          `Error al ejecutar la tarea: ${errorMessage}`,
          {
            duration: 5000,
          }
        )

        // Llamar callback de error si existe
        if (onError) {
          onError(err instanceof Error ? err : new Error(errorMessage))
        }
      } finally {
        setIsLoading(false)
      }
    },
    [taskId, endpoint, executeMethod, onSuccess, onError]
  )

  return {
    execute,
    isLoading,
    error,
  }
}

